import { useEffect, useContext, useState, useRef, useMemo } from "react";
import MapContext from "../MapContext";
import { MdCircle, MdOutlineHorizontalRule } from "react-icons/md";
import {
    BiCircle,
    BiRectangle,
    BiShapePolygon,
    BiText,
    BiEdit,
    BiTrash,
    BiEraser,
} from "react-icons/bi";
import { Button, ButtonGroup } from "@progress/kendo-react-buttons";
import { useSelector, useDispatch } from "react-redux";
import { setDrawTool } from "../../redux/toolSelectSlice";
import "./Markup.css";
import { Input, NumericTextBox } from "@progress/kendo-react-inputs";
import { DialogActionsBar, Dialog } from "@progress/kendo-react-dialogs";
import { getMapInteractionById, getMapLayerById } from "../utils/mapUtils";
import { createBox } from "ol/interaction/Draw";
import { Draw, Modify } from "ol/interaction";
import { Style, Fill, Stroke, Circle as CircleStyle, Text } from "ol/style";
import { Snapping } from "./Snapping";
import { setSnappingReady } from "../../redux/setConfigSlice";
import { PlotCoordinates } from "../PlotCoordinates/PlotCoordinates";

const Markup = () => {
    const { map } = useContext(MapContext);
    const drawTool = useSelector((state) => state.toolSelect.drawTool);
    const dispatch = useDispatch();
    const [textStyle, setTextStyle] = useState({
        strokeColor: "black",
        fillColor: "rgba(98, 158, 211, 0.5)",
        key: "blue",
    });
    const [lineStyle, setLineStyle] = useState(colors[0]);
    const [pointStyle, setPointStyle] = useState(colors[0]);
    const [polygonStyle, setPolygonStyle] = useState(colors[0]);
    const [textValue, setTextValue] = useState("Text");
    const [textSize, setTextSize] = useState(30);
    const [snapping, setSnapping] = useState(false);
    const [drawingsExist, setDrawingsExist] = useState();
    const [dialogVisible, setDialogVisible] = useState(false);
    const [reRender, setReRender] = useState(false);

    const handleSelectTool = (tool) => {
        dispatch(setDrawTool(tool));
    };

    const handleSetText = (e) => {
        setTextValue(e.value);
    };

    const handleSetTextSize = (e) => {
        setTextSize(e.value);
    };

    const handleDelete = () => {
        const drawLayer = getMapLayerById(map, "drawLayer");
        const coordinateSource = getMapLayerById(map, "coordinateLayer").getSource();
        coordinateSource.clear();
        drawLayer.getSource().clear();
        checkForFeatures();
    };

    const checkForFeatures = () => {
        const drawLayer = getMapLayerById(map, "drawLayer");
        const features = drawLayer.getSource().getFeatures();
        if (features.length > 0) {
            setDrawingsExist(true);
        } else if (features.length === 0) {
            setDrawingsExist(false);
            if (drawTool !== "Point") {
                dispatch(setDrawTool("Point"));
            }
        }
    };

    // something was causing interaction 'active' value to be set to false
    //   on component re-mount
    // wasn't false the first time mounting component after refresh
    // has to do with drawLayer
    useEffect(() => {
        setReRender(true);
    }, []);

    // update whether remove features options should exist
    useEffect(() => {
        // Check for features on mount
        checkForFeatures();
        // Add listener to check after each future interaction
        map.on("click", checkForFeatures);
        return () => map.un("click", checkForFeatures);
    }, []);

    // Runs for Erase tool
    useEffect(() => {
        if (drawTool !== "Erase") return;
        const handleEraseClick = async (e) => {
            const drawLayer = getMapLayerById(map, "drawLayer");
            const coordinateLayer = getMapLayerById(map, "coordinateLayer");
            const pixel = await map.getEventPixel(e.originalEvent);
            const feature = await drawLayer.getFeatures(pixel);
            const coordFeature = await coordinateLayer.getFeatures(pixel);
            coordinateLayer.getSource().removeFeature(coordFeature[0]);
            drawLayer.getSource().removeFeature(feature[0]);
            // updates remove features options
            checkForFeatures();
        };
        map.on("click", handleEraseClick);
        return () => map.un("click", handleEraseClick);
    }, [drawTool]);

    // Runs on initial load and when dependencies change:
    //   Style -- Text -- Tool -- layerVisibility
    useEffect(() => {
        // Escape useEffect and handle Erase in a separate useEffect
        if (drawTool === "Erase") return;
        const drawLayer = getMapLayerById(map, "drawLayer");

        // Assign style based on active tool
        const currentStyleObj =
            drawTool === "Circle" || drawTool === "Polygon" || drawTool === "Rectangle"
                ? polygonStyle
                : drawTool === "Text"
                  ? textStyle
                  : drawTool === "Point"
                    ? pointStyle
                    : lineStyle;

        // // Get Ol Style
        const olStyle = styleFunction(currentStyleObj, drawTool, textValue, textSize);

        // Reassign Rectangle and Text to Circle and Point tool types used for Draw
        const toolType =
            drawTool === "Rectangle"
                ? "Circle"
                : drawTool === "Text"
                  ? "Point"
                  : drawTool;

        // Get draw layer and update feautre style when drawn to map
        const updateFeatureStyle = (e) => {
            const feature = e.feature;
            feature.setStyle(olStyle);
        };
        const drawSource = drawLayer.getSource();
        drawSource.on("addfeature", updateFeatureStyle);

        // If tool is Rectangle use createBox() geometry function
        let geometryFunction = drawTool === "Rectangle" ? createBox() : undefined;
        // Create interaction
        const interaction =
            drawTool === "Modify"
                ? new Modify({
                      source: drawSource,
                  })
                : new Draw({
                      source: drawSource,
                      type: toolType,
                      style: olStyle,
                      geometryFunction: geometryFunction,
                  });

        // add draw interaction then add snap interactions
        interaction.setProperties({ Id: "markup-interaction" });
        map.addInteraction(interaction);
        dispatch(setSnappingReady(true));
        return () => {
            // unlisten for new feature
            drawSource.un("addfeature", updateFeatureStyle);
            const removeInteraction = getMapInteractionById(map, "markup-interaction");
            map.removeInteraction(removeInteraction);
            dispatch(setSnappingReady(false));
        };
    }, [
        lineStyle,
        pointStyle,
        textStyle,
        polygonStyle,
        drawTool,
        textValue,
        textSize,
        reRender,
    ]);

    return (
        <>
            <div className="title">
                <h3>Markup</h3>
            </div>
            <div className="option-item">
                <h4>Drawing tool</h4>
                <ButtonGroup width="100%" className="markup-tools">
                    {/* 
            create tool buttons 
            */}
                    {drawOptions.map((drawOption) => {
                        return (
                            <Button
                                key={drawOption.option}
                                onClick={() => handleSelectTool(drawOption.option)}
                                selected={drawTool === drawOption.option}
                                togglable>
                                <drawOption.icon />
                            </Button>
                        );
                    })}
                </ButtonGroup>
            </div>
            {drawTool === "Text" && (
                <div className="option-item">
                    <h4>Content</h4>
                    <div>Text to display:</div>
                    <Input
                        value={textValue}
                        onChange={handleSetText}
                        style={{ marginBottom: "8px" }}
                    />
                    <div>Font size:</div>
                    <NumericTextBox
                        style={{ maxWidth: "120px" }}
                        value={parseInt(textSize)}
                        onChange={handleSetTextSize}
                    />
                </div>
            )}
            <div className="option-item">
                <h4>Style</h4>
                {drawTool === "Point" && (
                    /*
            Create Point buttons
            */
                    <ButtonGroup width="100%" className="markup-styles">
                        {colors.map((colorOption) => {
                            return (
                                <Button
                                    key={colorOption.key}
                                    onClick={() => setPointStyle(colorOption)}
                                    selected={
                                        pointStyle && pointStyle.key === colorOption.key
                                    }
                                    togglable>
                                    <div className="layer-icon">
                                        <span
                                            className={"icon circle"}
                                            style={{
                                                backgroundColor: colorOption.strokeColor,
                                                padding: "1px",
                                            }}
                                        />
                                    </div>
                                </Button>
                            );
                        })}
                    </ButtonGroup>
                )}
                {drawTool === "Text" && (
                    /*
              Create Text style buttons
        */
                    <ButtonGroup width="100%" className="markup-styles">
                        {" "}
                        {colors.map((colorOption) => {
                            return (
                                <Button
                                    key={colorOption.key}
                                    onClick={() =>
                                        setTextStyle({
                                            strokeColor: "black",
                                            fillColor: colorOption.strokeColor,
                                            key: colorOption.key,
                                        })
                                    }
                                    selected={
                                        textStyle && textStyle.key === colorOption.key
                                    }
                                    togglable>
                                    <div className="layer-icon">
                                        <span
                                            className={"icon label"}
                                            style={{
                                                textShadow: `${getTextBorderStyle(
                                                    "black"
                                                )}`,
                                                color: colorOption.strokeColor,
                                                padding: "1px",
                                            }}>
                                            T
                                        </span>
                                    </div>
                                </Button>
                            );
                        })}
                        {colors.map((colorOption) => {
                            const key = `${colorOption.key}-outline`;
                            return (
                                <Button
                                    key={key}
                                    onClick={() =>
                                        setTextStyle({
                                            strokeColor: colorOption.strokeColor,
                                            fillColor: "white",
                                            key: key,
                                        })
                                    }
                                    selected={textStyle && textStyle.key === key}
                                    togglable>
                                    <div className="layer-icon">
                                        <span
                                            className={"icon label"}
                                            style={{
                                                textShadow: `${getTextBorderStyle(
                                                    colorOption.strokeColor
                                                )}`,
                                                color: "white",
                                                padding: "1px",
                                            }}>
                                            T
                                        </span>
                                    </div>
                                </Button>
                            );
                        })}
                    </ButtonGroup>
                )}
                {drawTool === "LineString" && (
                    /*
              Create LineString style buttons
        */
                    <ButtonGroup width="100%" className="markup-styles">
                        {" "}
                        {colors.map((colorOption) => {
                            return (
                                <Button
                                    key={colorOption.key}
                                    onClick={() => setLineStyle(colorOption)}
                                    selected={
                                        lineStyle && lineStyle.key === colorOption.key
                                    }
                                    togglable>
                                    <div className="layer-icon">
                                        <span
                                            className={"icon line"}
                                            style={{
                                                backgroundColor: colorOption.strokeColor,
                                                padding: "1px",
                                            }}
                                        />
                                    </div>
                                </Button>
                            );
                        })}
                    </ButtonGroup>
                )}
                {(drawTool === "Circle" ||
                    drawTool === "Polygon" ||
                    drawTool === "Rectangle") && (
                    /*
              Create styles buttons for circle, polygon, rectangle
        */
                    <ButtonGroup width="100%" className="markup-styles">
                        {" "}
                        {colors.map((colorOption) => {
                            return (
                                <Button
                                    key={colorOption.key}
                                    onClick={() => setPolygonStyle(colorOption)}
                                    selected={
                                        polygonStyle &&
                                        polygonStyle.key === colorOption.key
                                    }
                                    togglable>
                                    <div className="layer-icon">
                                        <span
                                            className={"icon fill"}
                                            style={{
                                                backgroundColor: colorOption.fillColor,
                                                border: `2px solid ${colorOption.strokeColor}`,
                                                padding: "1px",
                                            }}
                                        />
                                    </div>
                                </Button>
                            );
                        })}
                    </ButtonGroup>
                )}
            </div>
            {drawingsExist === true && (
                <div className="option-item">
                    <h4>Remove Drawings</h4>
                    <ButtonGroup className="remove-tools">
                        <Button onClick={() => setDialogVisible(true)}>
                            <BiTrash />
                            Delete
                        </Button>
                        <Button
                            onClick={() => handleSelectTool("Erase")}
                            selected={drawTool === "Erase"}
                            togglable>
                            <BiEraser />
                            Erase
                        </Button>
                    </ButtonGroup>
                    {dialogVisible && (
                        <Dialog
                            title={"Delete all drawings"}
                            onClose={() => setDialogVisible(false)}
                            id="markup-delete-dialog">
                            <p>Continue?</p>
                            <DialogActionsBar>
                                <Button onClick={() => setDialogVisible(false)}>
                                    No
                                </Button>
                                <Button
                                    onClick={() => {
                                        setDialogVisible(false);
                                        handleDelete();
                                    }}>
                                    Yes
                                </Button>
                            </DialogActionsBar>
                        </Dialog>
                    )}
                </div>
            )}
            <div className="option-item">
                <Snapping setSnapping={setSnapping} snapping={snapping} />
            </div>
        </>
    );
};

export default Markup;

const colors = [
    {
        strokeColor: "rgb(98, 158, 211)",
        fillColor: "rgba(98, 158, 211, 0.5)",
        key: "blue",
    },

    {
        strokeColor: "rgb(90, 176, 98)",
        fillColor: "rgba(90, 176, 98, 0.5)",
        key: "green",
    },

    {
        strokeColor: "rgb(151, 131, 184)",
        fillColor: "rgba(151, 131, 184, 0.5)",
        key: "purple",
    },
    {
        strokeColor: "rgb(204, 98, 94)",
        fillColor: "rgba(204, 98, 94, 0.5)",
        key: "red",
    },
    {
        strokeColor: "rgb(227, 124, 58)",
        fillColor: "rgba(227, 124, 58, 0.5)",
        key: "orange",
    },
    {
        strokeColor: "rgb(251, 240, 125)",
        fillColor: "rgba(251, 240, 125, 0.5)",
        key: "yellow",
    },
];

const drawOptions = [
    { option: "Point", icon: MdCircle },
    { option: "Text", icon: BiText },
    { option: "LineString", icon: MdOutlineHorizontalRule },
    { option: "Circle", icon: BiCircle },
    { option: "Polygon", icon: BiShapePolygon },
    { option: "Rectangle", icon: BiRectangle },
    { option: "Modify", icon: BiEdit },
];

const getTextBorderStyle = (borderColor) =>
    `1px 0 ${borderColor},
  -1px 0 ${borderColor},
  0 1px ${borderColor},
  0 -1px ${borderColor},
  1px 1px ${borderColor},
  -1px -1px ${borderColor},
  1px -1px ${borderColor},
  -1px 1px ${borderColor}`;

const styleFunction = (styleObj, drawTool, textValue, textSize) => {
    let style;
    if (drawTool === "Point" || drawTool === "LineString") {
        style = new Style({
            zIndex: 1000,
            fill: new Fill({
                color: styleObj.fillColor,
            }),
            stroke: new Stroke({
                color: styleObj.strokeColor,
                width: 2,
            }),
            image: new CircleStyle({
                radius: 5,
                fill: new Fill({
                    color: styleObj.strokeColor,
                }),
            }),
        });
    } else if (
        drawTool === "Circle" ||
        drawTool === "Rectangle" ||
        drawTool === "Polygon"
    ) {
        style = new Style({
            zIndex: 1000,
            fill: new Fill({
                color: styleObj.fillColor,
            }),
            stroke: new Stroke({
                color: styleObj.strokeColor,
                width: 2,
            }),
            image: new CircleStyle({
                radius: 5,
                fill: new Fill({
                    color: styleObj.strokeColor,
                }),
            }),
        });
    } else if (drawTool === "Text") {
        style = new Style({
            zIndex: 1000,
            text: new Text({
                text: textValue,
                font: `bold ${textSize}px Calibri,sans-serif`,
                fill: new Fill({
                    color: styleObj.fillColor,
                }),
                stroke: new Stroke({
                    color: styleObj.strokeColor,
                    width: 2,
                }),
            }),
            image: new CircleStyle({
                radius: 8,
                fill: new Fill({
                    color: "rgba(0, 0, 0, 0.0)",
                }),
            }),
        });
    }
    return style;
};
